import { Button, Card, Group, Stack, Text } from '@mantine/core'
import { IconPlus } from '@tabler/icons-react'
import { forwardRef, useImperativeHandle, useMemo } from 'react'

import { theme } from '@/configs/theme'
import { useFAQManagement } from '@/hooks/useFAQManagement'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { FAQEntry } from '@/screens/ContentModuleFormScreen/FAQEntry'

interface FAQSectionProps extends CMFormSection {
  type: 'cm' | 'step'
  disabled?: boolean
}

export interface FAQSectionRef {
  saveUnsavedFAQs: () => Promise<void>
}

export const FAQSection = forwardRef<FAQSectionRef, FAQSectionProps>(function FAQSection(props, ref) {
  const {
    approved: approvedFAQs,
    showValidationErrors,
    handleAddFAQ,
    handleUpdateFAQ,
    handleDeleteFAQ,
    saveUnsavedFAQs,
    handleGenerateFAQForId,
    handleGenerateFAQForIdWithGuide,
    handleCancelFAQGeneration,
    getFieldStateForFAQ,
  } = useFAQManagement(props)

  const isAnyFAQGenerating = useMemo(() => {
    return approvedFAQs.some(faq => getFieldStateForFAQ(faq.id).state === 'loading')
  }, [approvedFAQs, getFieldStateForFAQ])

  // Expose the save function to parent components
  useImperativeHandle(
    ref,
    () => ({
      saveUnsavedFAQs,
    }),
    [saveUnsavedFAQs],
  )

  return (
    <Stack gap="xs" mt="-xl">
      <Group justify="flex-end" align="center">
        <Button
          variant="light"
          leftSection={<IconPlus size={16} />}
          onClick={() => handleAddFAQ(false)}
          size="sm"
          disabled={props.disabled}>
          Add FAQ
        </Button>
      </Group>

      {approvedFAQs.length > 0 ? (
        <Stack gap="md" bg="gray.0" style={{ borderRadius: theme.radius.md }}>
          {approvedFAQs.map(faq => {
            const fieldState = getFieldStateForFAQ(faq.id)
            const isCurrentFAQGenerating = fieldState.state === 'loading'
            const isAnyOtherFAQGenerating = isAnyFAQGenerating && !isCurrentFAQGenerating

            return (
              <FAQEntry
                key={faq.id}
                faq={faq}
                onUpdate={handleUpdateFAQ}
                onDelete={handleDeleteFAQ}
                onGenerate={handleGenerateFAQForId}
                onGenerateWithGuide={handleGenerateFAQForIdWithGuide}
                onCancel={handleCancelFAQGeneration}
                fieldState={fieldState}
                showValidationErrors={showValidationErrors}
                isAnyOtherFAQGenerating={isAnyOtherFAQGenerating}
                disabled={props.disabled}
              />
            )
          })}
        </Stack>
      ) : (
        <Card p="xl" mt="md" bg="gray.0" radius="md" shadow="none" style={{ textAlign: 'center' }}>
          <Text c="dimmed">No FAQs added yet. Click &quot;Add FAQ&quot; to create one.</Text>
        </Card>
      )}
    </Stack>
  )
})
